// Fase 7B — sezione Google Calendar in "Impostazioni".
// - receptionist (readOnly=false, receptionistId=null): collega/gestisce il PROPRIO Google.
// - admin (readOnly=true, receptionistId=<id>): vede solo lo STATO della receptionist selezionata.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar, Check, Link2, Unlink, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cbApiErr } from "@/lib/callbacks";

export default function GoogleCalendarSection({ receptionistId = null, readOnly = false }) {
  const [params, setParams] = useSearchParams();
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (readOnly && !receptionistId) { setConn(null); setLoading(false); return; }
    try {
      const q = receptionistId ? `?receptionist_id=${receptionistId}` : "";
      const r = await api.get(`/agency/google-calendar/status${q}`);
      setConn(r.data);
    } catch { setConn({ connected: false, status: "disconnected" }); }
    finally { setLoading(false); }
  }, [readOnly, receptionistId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (readOnly) return;
    const g = params.get("google");
    if (!g) return;
    if (g === "connected") toast.success("Google Calendar collegato");
    else if (g === "reconnect") toast.warning("Ricollega Google Calendar");
    else toast.error("Collegamento Google non riuscito");
    params.delete("google"); setParams(params, { replace: true });
    load();
  }, [params, setParams, load, readOnly]);

  const connect = async () => {
    try { const r = await api.get("/agency/google-calendar/connect"); window.location.href = r.data.authorization_url; }
    catch (e) { toast.error(cbApiErr(e, "Impossibile avviare il collegamento")); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnettere Google Calendar? I richiami Memento restano invariati.")) return;
    try { await api.post("/agency/google-calendar/disconnect", {}); toast.success("Google disconnesso"); load(); }
    catch (e) { toast.error(cbApiErr(e, "Errore")); }
  };
  const toggleCal = async (id) => {
    const cur = new Set(conn.blocking_calendar_ids || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setSaving(true);
    try {
      const r = await api.patch("/agency/google-calendar/settings", { blocking_calendar_ids: Array.from(cur) });
      setConn(r.data);
    } catch (e) { toast.error(cbApiErr(e, "Errore")); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  if (readOnly && !receptionistId) return null;

  const reconnect = conn?.status === "reconnect_required";
  const memId = conn?.memento_calendar_id;
  const blocking = new Set(conn?.blocking_calendar_ids || []);

  return (
    <div className="sp-card p-5 mb-4" data-testid="gcal-section">
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="h-5 w-5 text-[color:var(--sp-brand)]" />
        <h2 className="font-display text-lg font-black">Google Calendar</h2>
      </div>
      <p className="text-xs text-[color:var(--sp-text-muted)] mb-3">
        Collegando Google, Memento eviterà di fissare richiami quando la receptionist è già occupata, e ogni richiamo
        comparirà nel suo calendario personale.
      </p>

      {!conn?.connected ? (
        <div>
          {reconnect && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Google Calendar deve essere ricollegato.
            </div>
          )}
          {readOnly ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold bg-slate-100 text-slate-500 border-slate-200" data-testid="gcal-status-readonly">
              Non collegato
            </span>
          ) : (
            <button data-testid="gcal-connect-btn" onClick={connect} className="sp-btn-primary inline-flex items-center gap-2">
              <Link2 className="h-4 w-4" /> Collega Google Calendar
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="gcal-status-readonly">
              <Check className="h-3 w-3" /> Collegato
            </span>
            <span className="text-[color:var(--sp-text-muted)]" data-testid="gcal-account">{conn.google_account_email}</span>
            {!readOnly && <button onClick={load} className="ml-auto sp-btn-ghost inline-flex items-center gap-1 text-xs"><RefreshCw className="h-3.5 w-3.5" /></button>}
          </div>

          <div>
            <div className="text-sm font-black mb-2">Calendari che bloccano la disponibilità</div>
            <div className="space-y-1.5">
              {(conn.available_calendars || []).map((c) => {
                const isMemento = c.id === memId;
                const checked = isMemento ? false : blocking.has(c.id);
                return (
                  <label key={c.id} data-testid={`gcal-cal-${c.id}`}
                    className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg border ${isMemento ? "opacity-60 border-dashed" : `border-[color:var(--sp-border)] ${readOnly ? "" : "cursor-pointer hover:bg-[color:var(--sp-subtle)]"}`}`}>
                    <input type="checkbox" disabled={isMemento || saving || readOnly}
                      checked={checked}
                      onChange={() => !isMemento && !readOnly && toggleCal(c.id)} />
                    <span className="flex-1 truncate">{c.summary || c.id}{c.primary ? " (principale)" : ""}</span>
                    {isMemento && <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-brand)]">Gestito da Memento</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {!readOnly && (
            <button data-testid="gcal-disconnect-btn" onClick={disconnect} className="inline-flex items-center gap-2 text-sm text-[color:var(--sp-danger)]">
              <Unlink className="h-4 w-4" /> Disconnetti Google
            </button>
          )}
        </div>
      )}
    </div>
  );
}
